import { useState, useEffect, useMemo, useRef, Fragment } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Accordion, AccordionSummary, AccordionDetails, Box, Button, Typography, useMediaQuery, useTheme, IconButton, Tooltip } from '@mui/material';

// Icons
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import MailIcon from '@mui/icons-material/Mail';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import ArticleIcon from '@mui/icons-material/Article';
import SettingsIcon from '@mui/icons-material/Settings';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import BookmarkBorderIcon from '@mui/icons-material/BookmarkBorder';
import HistoryIcon from '@mui/icons-material/History';

// Custom Components
import {
  Spinner,
  BrowserTitle,
  ProposalDetailsTable,
  ProposalSystemTable,
  ProposalAirflowSection,
  ProposalPriceTable,
  ProposalGuaranteesTable,
  ProposalCustomerInfoTable,
  ProposalDiscountsSection,
  ProposalRebatesSection,
  ProposalInstallationSection,
  ProposalScopeOfWorkSection,
  ProposalWarrantySection,
  ProposalJobOverviewTable,
  ProposalEquipmentOverviewTable,
  ProposalWorkDescriptionSection,
  ProposalJobPriceSection,
  EmailProposalModal,
  SaveSampleDialog,
  LoadSampleDialog,
  JobOverviewTemplateDialog,
  ProposalHistory,
} from '../components';

// Utils
import messages from '../static/messages';

// Services
import proposalsServices from '../services/proposalsServices';

// Hooks
import useAccordion from '../hooks/useAccordion';

// Styles
import { button } from '../static/theme-styles';

// External variables
const blocks = ['details', 'job_overview', 'system', 'scope_of_work', 'warranty', 'installation', 'discounts', 'rebates', 'total_price', 'guarantees', 'additional_info'];

const Proposal = () => {
  const [proposal, setProposal] = useState();
  const [systems, setSystems] = useState([]);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [saveSampleOpen, setSaveSampleOpen] = useState(false);
  const [loadSampleOpen, setLoadSampleOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [cloning, setCloning] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { accordionToggle } = useAccordion(blocks);

  // Calculate prices using useMemo to ensure they're always up to date
  const systemPrices = useMemo(() => {
    // Use new proposalSystems structure
    const proposalSystems = proposal?.proposalSystems || [];
    
    if (proposalSystems.length > 0) {
      return proposalSystems.map((system) => {
        // Calculate equipment total from new structure
        const equipments_price = (system.equipments || []).reduce(
          (total, eq) => total + (Number(eq.priceAtCreation) || 0),
          0
        );

        return {
          total: (Number(system.installationPrice) || 0) + equipments_price,
          instants_price: Number(system.instants) || 0,
          rebates_price: Number(system.rebates) || 0,
        };
      });
    } else {
      // Return array with one element with zeros for empty proposals
      return [
        {
          instants_price: 0,
          rebates_price: 0,
          total: 0,
        },
      ];
    }
  }, [proposal?.id, JSON.stringify(proposal?.proposalSystems?.map(s => ({
    id: s.id,
    rebates: s.rebates,
    instants: s.instants,
    installationPrice: s.installationPrice,
    equipmentsCount: (s.equipments || []).length,
    equipmentsTotal: (s.equipments || []).reduce((sum, eq) => sum + (Number(eq.priceAtCreation) || 0), 0)
  })))]);

  // Handler functions
  const handleEmailModalOpen = () => updateSystems().then(() => setEmailModalOpen(true));

  const handleEmailModalClose = () => setEmailModalOpen(false);

  const handleTemplateDialogOpen = () => setTemplateDialogOpen(true);

  const handleTemplateDialogClose = () => setTemplateDialogOpen(false);

  // Handle template application - update Project Overview
  const handleApplyTemplate = async (templateText, mode) => {
    try {
      let newJobOverview;
      
      if (mode === 'replace') {
        newJobOverview = templateText;
      } else if (mode === 'append') {
        const currentText = proposal?.jobOverview || '';
        newJobOverview = currentText + (currentText ? '\n\n' : '') + templateText;
      }

      const response = await proposalsServices.update(id, { jobOverview: newJobOverview });
      
      if (response.status === 200) {
        toast.success(mode === 'replace' ? 'Template applied' : 'Template appended');
        getProposalData();
      }
    } catch (error) {
      toast.error('Failed to apply template');
      console.log(error);
    }
  };

  const handleGeneratedTextInsert = async (generatedText) => {
    try {
      if (systems && systems.length > 0) {
        const updatedSystems = systems.map((system, index) => {
          if (index === 0) {
            return {
              ...system,
              installernotes: generatedText,
              usePromptGeneration: true,
            };
          }
          return system;
        });
        
        setSystems(updatedSystems);
        
        try {
          const response = await proposalsServices.update(id, {
            data: {
              systems: updatedSystems,
            },
          });

          if (response.status === 200) {
            toast.success('Generated text inserted and saved successfully');
            getProposalData();
          }
        } catch (error) {
          toast.error(messages.errors.error_try_again);
          console.log(error);
        }
      } else {
        toast.error('No systems found');
      }
    } catch (error) {
      console.error('Failed to insert generated text:', error);
      toast.error('Failed to insert generated text');
    }
  };

  const handleCloneProposal = async () => {
    if (cloning) return;
    setCloning(true);
    try {
      const response = await proposalsServices.clone(proposal.id);

      if (response.status === 200) {
        toast.success(`Proposal cloned successfully as ${response.data.publicId}`);
        navigate(`/proposals/${response.data.id}`);
      }
    } catch (error) {
      console.log(error);
      if (error.response?.status === 404) toast.error('Proposal not found');
      else if (error.response?.status === 409) toast.error(error.response.data.message);
      else toast.error(messages.errors.error_try_again);
    } finally {
      setCloning(false);
    }
  };

  // Async functions
  const getProposalData = async () => {
    try {
      const response = await proposalsServices.getProposalById(id);
      setProposal(response.data);
    } catch (error) {
      if (error?.response?.data?.status === 404) {
        navigate('/404');
      } else {
        toast.error(messages.errors.error_data_loaning);
        console.log(error);
      }
    }
  };

  const updateSystems = async () => {
    try {
      const response = await proposalsServices.update(id, {
        data: {
          systems: systems,
        },
      });

      if (response.status === 200) {
        toast.success('Systems successfully updated');
        getProposalData();
      }
    } catch (error) {
      toast.error(messages.errors.error_try_again);
      console.log(error);
    }
  };

  const seeProposal = async () => {
    // Open window BEFORE async call to avoid popup blocker
    // Browsers block window.open() called after await
    const newWindow = window.open('', '_blank');
    
    try {
      await updateSystems();
      // Navigate the already-opened window to the proposal view
      if (newWindow) {
        newWindow.location.href = `/proposals/view/${proposal.id}`;
      }
    } catch (error) {
      // Close the empty window if there was an error
      if (newWindow) {
        newWindow.close();
      }
      throw error;
    }
  };

  // Hooks
  useEffect(() => {
    setProposal(null);
    getProposalData();
  }, [id]);

  // Auto-refresh the signature status while a proposal is unsigned. The customer
  // usually signs on the public proposal link (a separate page/device), so when
  // the operator returns to this editor tab we re-fetch to pick up a fresh
  // signature — which unblocks the Installation Date picker without a manual
  // reload. We only refetch while unsigned, so signed proposals don't refetch.
  const proposalRef = useRef(proposal);
  const getProposalDataRef = useRef(getProposalData);
  useEffect(() => { proposalRef.current = proposal; }, [proposal]);
  useEffect(() => { getProposalDataRef.current = getProposalData; });

  useEffect(() => {
    const maybeRefresh = () => {
      if (document.visibilityState !== 'visible') return;
      const p = proposalRef.current;
      if (p && !(p.signatureId || p.signature)) {
        getProposalDataRef.current();
      }
    };
    window.addEventListener('focus', maybeRefresh);
    document.addEventListener('visibilitychange', maybeRefresh);
    return () => {
      window.removeEventListener('focus', maybeRefresh);
      document.removeEventListener('visibilitychange', maybeRefresh);
    };
  }, []);


  // Check for data loading
  if (!proposal) return <Spinner />;

  return (
    <Fragment>
      <Box component="section">
        <BrowserTitle title="Proposal | Airmaxx Pro" />
        {/* Action Buttons - Desktop: full buttons, Mobile: icons only */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: { xs: 0.5, md: 2 },
            mb: 2,
          }}
        >
          {/* Back Button - always show text */}
          <Button
            variant="text"
            size={isMobile ? 'small' : 'medium'}
            onClick={() =>
              navigate(
                `/proposals${
                  location.state
                    ? `?page=${location?.state?.page}&per_page=${location?.state?.rowsPerPage}${
                        location?.state?.query ? `&query=${encodeURIComponent(location.state.query)}` : ''
                      }`
                    : ''
                }`,
              )
            }
            startIcon={<KeyboardBackspaceIcon />}
          >
            {isMobile ? 'Back' : 'Back to Proposals'}
          </Button>

          {/* Mobile: Icon buttons */}
          {isMobile ? (
            <>
              <Tooltip title="Preview proposal">
                <IconButton onClick={seeProposal} size="small">
                  <PictureAsPdfIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Email proposal">
                <IconButton onClick={handleEmailModalOpen} size="small">
                  <MailIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title={cloning ? "Cloning..." : "Clone proposal"}>
                <span>
                  <IconButton onClick={handleCloneProposal} size="small" disabled={cloning}>
                    <FileCopyIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Project Overview Templates">
                <IconButton onClick={handleTemplateDialogOpen} size="small" sx={{ color: '#2196f3' }}>
                  <ArticleIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Save Sample">
                <IconButton onClick={() => setSaveSampleOpen(true)} size="small" sx={{ color: '#ff9800' }}>
                  <BookmarkIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Load Sample">
                <IconButton onClick={() => setLoadSampleOpen(true)} size="small" sx={{ color: '#4caf50' }}>
                  <BookmarkBorderIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Change History">
                <IconButton onClick={() => setHistoryOpen(true)} size="small" sx={{ color: '#9c27b0' }}>
                  <HistoryIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            /* Desktop: Full buttons */
            <>
              <Button onClick={seeProposal} variant="text" endIcon={<PictureAsPdfIcon />}>
                Preview proposal
              </Button>

              <Button onClick={handleEmailModalOpen} variant="text" endIcon={<MailIcon />}>
                Email this proposal
              </Button>

              <Button onClick={handleCloneProposal} variant="text" endIcon={<FileCopyIcon />} disabled={cloning}>
                {cloning ? 'Cloning...' : 'Clone this proposal'}
              </Button>

              <Button onClick={handleTemplateDialogOpen} variant="text" endIcon={<ArticleIcon />} sx={{ color: '#2196f3' }}>
                Project Overview Templates
              </Button>

              <Button component={Link} to="/job-overview-templates" variant="text" endIcon={<SettingsIcon />} sx={{ color: '#757575' }}>
                Manage Templates
              </Button>


              <Button onClick={() => setSaveSampleOpen(true)} variant="text" endIcon={<BookmarkIcon />} sx={{ color: '#ff9800' }}>
                Save Sample
              </Button>

              <Button onClick={() => setLoadSampleOpen(true)} variant="text" endIcon={<BookmarkBorderIcon />} sx={{ color: '#4caf50' }}>
                Load Sample
              </Button>

              <Button onClick={() => setHistoryOpen(true)} variant="text" endIcon={<HistoryIcon />} sx={{ color: '#9c27b0' }}>
                Change History
              </Button>
            </>
          )}
        </Box>

        {/* Proposal Details */}
        <Accordion
          defaultExpanded
          onChange={() => accordionToggle('details')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">
              Proposal Details
              {proposal.proposalType === 'Service' && (
                <Typography component="span" sx={{ ml: 1, px: 1, py: 0.25, backgroundColor: '#ed6c02', color: 'white', borderRadius: 1, fontSize: '0.75rem' }}>
                  SERVICE
                </Typography>
              )}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ProposalDetailsTable {...{ proposal, id, refresh: getProposalData }} />
          </AccordionDetails>
        </Accordion>

        {/* Installation Proposal: Project Overview */}
        {proposal.proposalType !== 'Service' && (
          <Accordion
            defaultExpanded
            onChange={() => accordionToggle('job_overview')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Project Overview</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ProposalJobOverviewTable {...{ proposal, id, refresh: getProposalData }} />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Service Proposal: Equipment Overview */}
        {proposal.proposalType === 'Service' && (
          <Accordion
            defaultExpanded
            onChange={() => accordionToggle('equipment_overview')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Equipment Overview</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ProposalEquipmentOverviewTable {...{ proposal, id, refresh: getProposalData }} />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Installation Proposal: Systems */}
        {proposal.proposalType !== 'Service' && (
          <Accordion
            defaultExpanded
            onChange={() => accordionToggle('system')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Systems</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ProposalSystemTable proposal={proposal} id={id} refresh={getProposalData} />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Installation Proposal: Airflow */}
        {proposal.proposalType !== 'Service' && (
          <Accordion
            defaultExpanded
            onChange={() => accordionToggle('airflow')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Airflow</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ProposalAirflowSection proposal={proposal} id={id} refresh={getProposalData} />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Installation Proposal: Scope of Work */}
        {proposal.proposalType !== 'Service' && (
          <Accordion
            defaultExpanded
            onChange={() => accordionToggle('scope_of_work')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Scope of Work</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ProposalScopeOfWorkSection {...{ proposal, id, refresh: getProposalData }} />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Service Proposal: Work Description */}
        {proposal.proposalType === 'Service' && (
          <Accordion
            defaultExpanded
            onChange={() => accordionToggle('work_description')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Work Description</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ProposalWorkDescriptionSection {...{ proposal, id, refresh: getProposalData }} />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Warranty (both types) */}
        <Accordion
          defaultExpanded
          onChange={() => accordionToggle('warranty')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">Warranty</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ProposalWarrantySection {...{ proposal, id, refresh: getProposalData }} />
          </AccordionDetails>
        </Accordion>

        {/* Service Proposal: Job Price */}
        {proposal.proposalType === 'Service' && (
          <Accordion
            defaultExpanded
            onChange={() => accordionToggle('job_price')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Job Price</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ProposalJobPriceSection {...{ proposal, id, refresh: getProposalData }} />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Discounts (both types) */}
        <Accordion
          defaultExpanded
          onChange={() => accordionToggle('discounts')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">Discounts</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ProposalDiscountsSection {...{ proposal, id, refresh: getProposalData }} />
          </AccordionDetails>
        </Accordion>

        {/* Installation Proposal: Rebates */}
        {proposal.proposalType !== 'Service' && (
          <Accordion
            defaultExpanded
            onChange={() => accordionToggle('rebates')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Rebates</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ProposalRebatesSection {...{ proposal, id, refresh: getProposalData }} />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Installation Proposal: Installation Price */}
        {proposal.proposalType !== 'Service' && (
          <Accordion
            defaultExpanded
            onChange={() => accordionToggle('installation')}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body1">Installation Price</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <ProposalInstallationSection {...{ proposal, id, refresh: getProposalData }} />
            </AccordionDetails>
          </Accordion>
        )}

        {/* Total Price (both types) */}
        <Accordion
          defaultExpanded
          onChange={() => accordionToggle('total_price')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">Total Price</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ProposalPriceTable {...{ proposal, id, refresh: getProposalData, prices: systemPrices }} />
          </AccordionDetails>
        </Accordion>

        {/* Maxx Guarantees (both types) */}
        <Accordion
          defaultExpanded
          onChange={() => accordionToggle('guarantees')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">Maxx Guarantees</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ProposalGuaranteesTable {...{ proposal, id, refresh: getProposalData }} />
          </AccordionDetails>
        </Accordion>

        {/* Information for Customer  */}
        <Accordion
          defaultExpanded
          onChange={() => accordionToggle('additional_info')}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body1">Information for Customer</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ProposalCustomerInfoTable {...{ proposal, id, refresh: getProposalData }} />
          </AccordionDetails>
        </Accordion>

        <Box sx={{ mt: 2 }}>
          <Button
            onClick={handleEmailModalOpen}
            size="large"
            variant="contained"
            sx={{ ...button('green', 'secondary'), fontWeight: 800, textTransform: 'uppercase' }}
            endIcon={<MailIcon />}
          >
            Email this proposal
          </Button>
          <Button
            onClick={seeProposal}
            size="large"
            variant="contained"
            sx={{
              ...button('secondary'),
              ml: { md: 2, xs: 0 },
              mt: { md: 0, xs: 2 },
              fontWeight: 800,
              textTransform: 'uppercase',
            }}
            endIcon={<PictureAsPdfIcon />}
          >
            Preview proposal
          </Button>
        </Box>
      </Box>

      {emailModalOpen && <EmailProposalModal isOpen={emailModalOpen} onClose={handleEmailModalClose} data={proposal} />}
      
      <JobOverviewTemplateDialog 
        open={templateDialogOpen} 
        onClose={handleTemplateDialogClose}
        currentJobOverview={proposal?.jobOverview}
        onApplyTemplate={handleApplyTemplate}
      />


      {/* Save Sample Dialog */}
      <SaveSampleDialog
        open={saveSampleOpen}
        onClose={() => setSaveSampleOpen(false)}
        proposal={proposal}
        onSaveSuccess={() => toast.success('Sample saved!')}
      />

      {/* Change History Dialog */}
      <ProposalHistory
        proposalId={id}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
      />

      {/* Load Sample Dialog */}
      <LoadSampleDialog
        open={loadSampleOpen}
        onClose={() => setLoadSampleOpen(false)}
        proposalId={id}
        proposalType={proposal?.proposalType}
        onApply={() => { setLoadSampleOpen(false); getProposalData(); }}
      />
    </Fragment>
  );
};

export default Proposal;
